import { c as _c } from "react/compiler-runtime";
import { useInjection } from 'inversify-react';
import { createContext, useMemo, type ReactNode } from 'react';
import { useGenerateTargetSeries, type TargetSeries } from '@/components/design/hooks/use-target-series-generators';
import { useGenerateDesignedKeyboardFromSeries } from '@/hooks/keyboard/use-generate-designed-keyboard-from-series';
import { useMeasuredKeyboard } from '@/hooks/keyboard/use-measured-keyboard';
import { touchWeightAnalyzerRequirementsSymbol, type TouchWeightAnalyzerRequirements } from '@/lib/piano/touch-design/touch-weight-analyzer/touch-weight-analyzer-requirements';
import type { TouchWeightAnalyzedKeyboard } from '@/lib/piano/touch-design/touch-weight-key-analysis';
import { touchWeightPreviewerRequirementsSymbol, type TouchWeightPreviewerRequirements } from '@/lib/piano/touch-design/touch-weight-previewer/touch-weight-previewer.requirements';
export interface KeyboardAnalysisContextValue {
  /** The measured keyboard, analyzed key by key. */
  analyzedKeyboard: TouchWeightAnalyzedKeyboard;
  /** The same keyboard once the four targets of the design are applied. */
  designedKeyboard: TouchWeightAnalyzedKeyboard;
  targetSeries: TargetSeries;
}
export const KeyboardAnalysisContext = createContext<KeyboardAnalysisContextValue | undefined>(undefined);

/**
 * Analyzes the measured keyboard and designs it, once for the whole page.
 *
 * The layout, the design banner, its printed recap, the page and its design
 * panel all read these keyboards. Built by each of them, a click on a Design
 * page would run the analysis of every key and the design pipeline four
 * times over.
 */
export const KeyboardAnalysisProvider = t0 => {
  const $ = _c(18);
  const {
    children
  } = t0;
  const touchWeightAnalyzer = useInjection(touchWeightAnalyzerRequirementsSymbol);
  const touchWeightPreviewer = useInjection(touchWeightPreviewerRequirementsSymbol);
  const measuredKeyboard = useMeasuredKeyboard();
  let t1;
  if ($[0] !== measuredKeyboard || $[1] !== touchWeightAnalyzer) {
    let t2;
    if ($[3] !== touchWeightAnalyzer) {
      t2 = key => touchWeightAnalyzer.analyzeKey(key);
      $[3] = touchWeightAnalyzer;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = measuredKeyboard.map(t2);
    $[0] = measuredKeyboard;
    $[1] = touchWeightAnalyzer;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const analyzedKeyboard = t1;
  const targetSeries = useGenerateTargetSeries(analyzedKeyboard);
  const {
    generateDesignedKeyboardFromSeries
  } = useGenerateDesignedKeyboardFromSeries(analyzedKeyboard);
  let t2;
  if ($[5] !== generateDesignedKeyboardFromSeries || $[6] !== targetSeries || $[7] !== touchWeightPreviewer) {
    let t3;
    if ($[9] !== touchWeightPreviewer) {
      t3 = key_0 => touchWeightPreviewer.computeTouchWeight(key_0);
      $[9] = touchWeightPreviewer;
      $[10] = t3;
    } else {
      t3 = $[10];
    }
    t2 = generateDesignedKeyboardFromSeries(targetSeries).map(t3);
    $[5] = generateDesignedKeyboardFromSeries;
    $[6] = targetSeries;
    $[7] = touchWeightPreviewer;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const designedKeyboard = t2;
  let t3;
  if ($[11] !== analyzedKeyboard || $[12] !== designedKeyboard || $[13] !== targetSeries) {
    t3 = {
      analyzedKeyboard,
      designedKeyboard,
      targetSeries
    };
    $[11] = analyzedKeyboard;
    $[12] = designedKeyboard;
    $[13] = targetSeries;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  const value = t3;
  let t4;
  if ($[15] !== children || $[16] !== value) {
    t4 = <KeyboardAnalysisContext.Provider value={value}>{children}</KeyboardAnalysisContext.Provider>;
    $[15] = children;
    $[16] = value;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  return t4;
};